import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faQuestionCircle,
  faSync,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import authProfileService from '../../services/authProfileService';

/**
 * AuthProfileTokenStatus - Display token expiry status and refresh button
 */
const AuthProfileTokenStatus = ({ profile, onRefreshed, compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (profile && (profile.authType === 'jwt' || profile.authType === 'bearer')) {
      loadStatus();
    }
  }, [profile]);

  const loadStatus = async () => {
    if (!profile) return;

    setLoading(true);
    setError(null);

    try {
      const result = await authProfileService.getTokenStatus(profile._id);
      setStatus(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load token status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);

    try {
      const result = await authProfileService.refreshToken(profile._id);

      if (result.success) {
        await loadStatus();
        if (onRefreshed) {
          onRefreshed(result);
        }
      } else {
        setError(result.message || 'Token refresh failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Token refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  if (!profile || (profile.authType !== 'jwt' && profile.authType !== 'bearer')) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-xs">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Checking token...</span>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="flex items-center gap-2 text-red-500 text-xs">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <span>{error}</span>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  // Get status icon and color
  const getStatusIcon = () => {
    switch (status.status) {
      case 'valid':
        return faCheckCircle;
      case 'expired':
        return faExclamationTriangle;
      case 'invalid':
        return faExclamationTriangle;
      case 'missing':
        return faQuestionCircle;
      default:
        return faClock;
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'valid':
        if (status.timeRemaining && status.timeRemaining < 300) {
          return 'text-yellow-500';
        }
        return 'text-green-500';
      case 'expired':
      case 'invalid':
        return 'text-red-500';
      case 'missing':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };

  const getBadgeColor = () => {
    switch (status.status) {
      case 'valid':
        if (status.timeRemaining && status.timeRemaining < 300) {
          return 'bg-yellow-100 text-yellow-700';
        }
        return 'bg-green-100 text-green-700';
      case 'expired':
      case 'invalid':
        return 'bg-red-100 text-red-700';
      case 'missing':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (compact) {
    // Compact mode for profile list
    return (
      <div className="flex items-center gap-2">
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${getBadgeColor()}`}>
          <FontAwesomeIcon icon={getStatusIcon()} className="text-xs" />
          {status.timeRemainingHuman || status.status}
        </div>
        {status.canRefresh && (status.status === 'expired' || (status.timeRemaining && status.timeRemaining < 60)) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={refreshing}
            className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 disabled:opacity-50"
            title="Refresh token"
          >
            {refreshing ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSync} />
            )}
          </button>
        )}
      </div>
    );
  }

  // Full mode for detail view
  return (
    <div className="border border-gray-300 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Token Status</span>
        {status.canRefresh && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
          >
            {refreshing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Refreshing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSync} />
                Refresh Token
              </>
            )}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={getStatusIcon()} className={`text-lg ${getStatusColor()}`} />
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {status.message}
          </div>
          {status.timeRemainingHuman && (
            <div className="text-xs text-gray-600">
              {status.status === 'expired' ? 'Expired' : 'Expires in'}: {status.timeRemainingHuman}
            </div>
          )}
        </div>
      </div>

      {status.expiresAt && (
        <div className="text-xs text-gray-600">
          <span className="font-semibold">Expires at:</span>{' '}
          {new Date(status.expiresAt).toLocaleString()}
        </div>
      )}

      {status.lastRefreshed && (
        <div className="text-xs text-gray-600">
          <span className="font-semibold">Last refreshed:</span>{' '}
          {new Date(status.lastRefreshed).toLocaleString()}
        </div>
      )}

      {status.isJwt && status.payload && (
        <div className="text-xs text-gray-600">
          <span className="font-semibold">JWT Claims:</span>
          <div className="mt-1 bg-gray-100 p-2 rounded font-mono text-xs max-h-32 overflow-auto">
            <pre>{JSON.stringify(status.payload, null, 2)}</pre>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      {!status.canRefresh && (status.status === 'expired' || status.status === 'invalid') && (
        <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
          Token refresh is not enabled for this profile. You may need to manually update the token or configure refresh settings.
        </div>
      )}
    </div>
  );
};

export default AuthProfileTokenStatus;
